import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  UserManager,
  WebStorageStateStore,
  type User as OidcUser,
} from 'oidc-client-ts'

import {
  bankAuthApi,
  setBankAccessToken,
  type BankActorPrincipal,
} from '@/api/bank'

export interface BankUser {
  id: string
  name?: string
  email?: string
  role: 'bank_admin' | 'bank_officer'
  company_id: string
  branch_ids: string[]
  domain_roles: string[]
}

interface BankAuthContextValue {
  user: BankUser | null
  principal: BankActorPrincipal | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  login: () => Promise<void>
  logout: () => Promise<void>
  resetIdleTimer: () => void
}

export const BankAuthContext = createContext<
  BankAuthContextValue | undefined
>(
  undefined,
)

export function bankOidcEnabled(): boolean {
  return (
    String(import.meta.env.VITE_BANK_IMPORT_LC_ENABLED || '').toLowerCase() ===
    'true'
  )
}

function requiredBankOidcSetting(name: string, value: string | undefined): string {
  const normalized = String(value || '').trim()
  if (!normalized) {
    throw new Error(`Missing required bank OIDC setting: ${name}`)
  }
  return normalized
}

function createBankUserManager(): UserManager | null {
  if (!bankOidcEnabled()) {
    return null
  }

  const authority = requiredBankOidcSetting(
    'VITE_BANK_OIDC_ISSUER',
    import.meta.env.VITE_BANK_OIDC_ISSUER,
  )
  const clientId = requiredBankOidcSetting(
    'VITE_BANK_OIDC_CLIENT_ID',
    import.meta.env.VITE_BANK_OIDC_CLIENT_ID,
  )
  const redirectUri = requiredBankOidcSetting(
    'VITE_BANK_OIDC_REDIRECT_URI',
    import.meta.env.VITE_BANK_OIDC_REDIRECT_URI,
  )

  return new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: redirectUri,
    response_type: 'code',
    disablePKCE: false,
    scope: String(
      import.meta.env.VITE_BANK_OIDC_SCOPE || 'openid profile email',
    ),
    userStore: new WebStorageStateStore({ store: sessionStorage }),
    stateStore: new WebStorageStateStore({ store: sessionStorage }),
    automaticSilentRenew: false,
    loadUserInfo: true,
  })
}

function toBankUser(principal: BankActorPrincipal): BankUser {
  return {
    id: principal.actor_id,
    role: principal.roles.includes('operations_admin')
      ? 'bank_admin'
      : 'bank_officer',
    company_id: principal.bank_id,
    branch_ids: principal.branch_ids,
    domain_roles: principal.roles,
  }
}

function callbackResponseKind(): 'signin' | 'signout' | null {
  if (typeof window === 'undefined') {
    return null
  }
  const query = new URLSearchParams(window.location.search)
  if (query.has('code') || query.has('error')) return 'signin'
  if (query.has('state')) return 'signout'
  return null
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Bank sign-in could not be completed'
}

interface BankAuthProviderProps {
  children: ReactNode
}

export function BankAuthProvider({ children }: BankAuthProviderProps) {
  const navigate = useNavigate()
  const [principal, setPrincipal] = useState<BankActorPrincipal | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const managerResult = useMemo(() => {
    try {
      return {
        manager: createBankUserManager(),
        configurationError: null,
      }
    } catch (configurationError) {
      return {
        manager: null,
        configurationError: errorMessage(configurationError),
      }
    }
  }, [])
  const manager = managerResult.manager

  const clearAuthorization = useCallback(() => {
    setBankAccessToken(null)
    setPrincipal(null)
  }, [])

  const resolveOidcUser = useCallback(
    async (oidcUser: OidcUser | null): Promise<BankActorPrincipal | null> => {
      if (!manager || !oidcUser || oidcUser.expired || !oidcUser.access_token) {
        clearAuthorization()
        return null
      }

      try {
        setBankAccessToken(oidcUser.access_token)
        const resolved = await bankAuthApi.resolvePrincipal()
        setPrincipal(resolved)
        setError(null)
        return resolved
      } catch (resolutionError) {
        clearAuthorization()
        setError(errorMessage(resolutionError))
        await manager.removeUser()
        return null
      }
    },
    [clearAuthorization, manager],
  )

  useEffect(() => {
    let active = true
    if (!manager) {
      if (managerResult.configurationError) {
        setError(managerResult.configurationError)
      }
      setIsLoading(false)
      return () => {
        active = false
      }
    }

    const onUserLoaded = (oidcUser: OidcUser) => {
      setIsLoading(true)
      void resolveOidcUser(oidcUser).finally(() => {
        if (active) setIsLoading(false)
      })
    }
    const onUserUnavailable = () => {
      clearAuthorization()
      if (active) setIsLoading(false)
    }

    manager.events.addUserLoaded(onUserLoaded)
    manager.events.addUserUnloaded(onUserUnavailable)
    manager.events.addAccessTokenExpired(onUserUnavailable)

    const initialize = async () => {
      try {
        const callbackKind = callbackResponseKind()
        if (callbackKind === 'signout') {
          await manager.signoutRedirectCallback()
          await manager.removeUser()
          clearAuthorization()
          return
        }
        const oidcUser = callbackKind === 'signin'
          ? await manager.signinRedirectCallback()
          : await manager.getUser()
        const resolved = await resolveOidcUser(oidcUser)
        if (
          active &&
          resolved &&
          (callbackKind === 'signin' ||
            window.location.pathname.endsWith('/bank-dashboard/login'))
        ) {
          navigate('/lcopilot/bank-dashboard', { replace: true })
        }
      } catch (initializationError) {
        clearAuthorization()
        setError(errorMessage(initializationError))
        await manager.removeUser()
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void initialize()
    return () => {
      active = false
      manager.events.removeUserLoaded(onUserLoaded)
      manager.events.removeUserUnloaded(onUserUnavailable)
      manager.events.removeAccessTokenExpired(onUserUnavailable)
    }
  }, [
    clearAuthorization,
    manager,
    managerResult.configurationError,
    navigate,
    resolveOidcUser,
  ])

  const login = useCallback(async () => {
    if (!manager) {
      throw new Error(
        error || 'Bank Import LC sign-in is not configured',
      )
    }
    setError(null)
    await manager.signinRedirect()
  }, [error, manager])

  const logout = useCallback(async () => {
    clearAuthorization()
    if (manager) {
      await manager.signoutRedirect()
      return
    }
    navigate('/lcopilot/bank-dashboard/login', { replace: true })
  }, [clearAuthorization, manager, navigate])

  const value = useMemo<BankAuthContextValue>(
    () => ({
      user: principal ? toBankUser(principal) : null,
      principal,
      isLoading,
      isAuthenticated: principal !== null,
      error,
      login,
      logout,
      // OIDC session lifetime is owned by the bank IdP. Kept as a no-op for
      // legacy consumers while the dashboard migrates off local idle timers.
      resetIdleTimer: () => undefined,
    }),
    [error, isLoading, login, logout, principal],
  )

  return (
    <BankAuthContext.Provider value={value}>
      {children}
    </BankAuthContext.Provider>
  )
}

export function useBankAuth(): BankAuthContextValue {
  const context = useContext(BankAuthContext)
  if (!context) {
    throw new Error('useBankAuth must be used within a BankAuthProvider')
  }
  return context
}

export function BankAuthenticatedRoute({
  children,
}: {
  children: ReactNode
}) {
  const { isAuthenticated, isLoading } = useBankAuth()

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        role="status"
      >
        Resolving bank identity…
      </div>
    )
  }
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/lcopilot/bank-dashboard/login"
        replace
      />
    )
  }
  return <>{children}</>
}
